import { useCallback, useEffect, useRef, useState } from 'react';
import { useRealtimeFeed } from 'services/client/realtime';
import type { DarkPoolRow, RtSnapshotBody } from 'constants/payloads/realtime';
import { mergeDarkPoolRows as merge } from 'components/DarkPoolTable/mergeDarkPoolRows';

const FLUSH_MS = 500;

export interface TickerDarkPoolRealtime {
  rows: DarkPoolRow[];
  sessionDate: string | null;
  status: string;
}

/**
 * Ticker dark-pool live bucket (spec §11.3, Phase 4). The DO owns the current
 * session for one ticker (server-side ticker-filtered); the section merges it
 * with the Hasura history, disjoint by date — same pattern as the ticker flow
 * split. Dark pool is paid-only, so `enabled` must gate on dashboard access.
 */
export function useTickerDarkPoolRealtime(opts: {
  ticker: string | null | undefined;
  enabled: boolean;
}): TickerDarkPoolRealtime {
  const { ticker, enabled } = opts;
  const [rows, setRows] = useState<DarkPoolRow[]>([]);
  const [sessionDate, setSessionDate] = useState<string | null>(null);
  const buffer = useRef<DarkPoolRow[]>([]);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flush = useCallback(() => {
    timer.current = null;
    if (!buffer.current.length) return;
    const batch = buffer.current;
    buffer.current = [];
    setRows((prev) => merge(prev, batch));
  }, []);

  const schedule = useCallback(() => {
    if (timer.current) return;
    timer.current = setTimeout(flush, FLUSH_MS);
  }, [flush]);

  const upper = ticker ? ticker.toUpperCase() : undefined;

  const { status } = useRealtimeFeed({
    feed: 'darkpool',
    enabled: enabled && !!upper,
    params: { tickers: upper ? [upper] : undefined },
    onSnapshot: (body, hello) => {
      const snap = body as RtSnapshotBody & { rows?: DarkPoolRow[] };
      buffer.current = [];
      setRows(merge([], (snap.rows as DarkPoolRow[]) ?? []));
      if (hello?.sessionDate) setSessionDate(hello.sessionDate);
    },
    onDelta: (delta) => {
      buffer.current.push(...(delta.rows as DarkPoolRow[]));
      if (delta.sessionDate) setSessionDate(delta.sessionDate);
      schedule();
    },
    onReset: (reset) => {
      buffer.current = [];
      setRows([]);
      setSessionDate(reset.sessionDate);
    },
  });

  useEffect(() => {
    buffer.current = [];
    setRows([]);
    return () => {
      if (timer.current) clearTimeout(timer.current);
      timer.current = null;
    };
  }, [upper, enabled]);

  // Clear the live bucket on fallback so the 11-min retry can't re-show stale rows as live;
  // the history/merge covers the gap, a retry-success re-snapshots fresh (§17.12 R4).
  useEffect(() => {
    if (status === 'fallback') {
      buffer.current = [];
      setRows([]);
    }
  }, [status]);

  return { rows, sessionDate, status };
}
